/**
 * /kiosk — customer walk-in wizard.
 *
 * Replaces the Phase 0 placeholder. Four steps, then a result screen:
 *
 *   service → add-ons → barber → contact → [assigned | stay-or-go]
 *
 * Shorter and louder than /book: this runs on a landscape tablet bolted to
 * the front desk, tapped by someone standing up with a coat on. Bigger
 * targets, fewer fields, no SMS consent (A2P isn't live — we say we'll
 * call instead of promising a text we can't legally send yet).
 *
 * All copy from src/content, all facts from useShopConfig. Step 3 gates on
 * live clock-in state, so the wizard cannot submit into an empty shop.
 */

import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getFns } from '../../lib/firebase';
import { useCopy } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';
import { useClockedIn } from '../../hooks/useShopState';

import QrHandoff from '../../components/kiosk/QrHandoff';
import IdlePrompt from '../../components/kiosk/IdlePrompt';
import { walkInStatus } from '../../lib/walkInStatus';
import { useKioskGround, KIOSK_PAPER, KIOSK_NAVY } from '../../components/kiosk/useKioskGround';

type Step = 'service' | 'addOns' | 'barber' | 'contact' | 'result';

interface CheckInResult {
  walkInId: string;
  shortId: string;
  initialAssignment: string | null;
  queuePosition: number | null;
}

/** (813) 555-0123 as they type; +18135550123 on submit. */
function formatPhone(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
const toE164 = (display: string): string => `+1${display.replace(/\D/g, '')}`;

export default function WalkInWizard() {
  const copy = useCopy();
  const c = copy.kioskWalkIn;
  const { t, kiosk, shop, serviceName, services, addOns, barbers, hours, formatDuration } = useShopConfig();
  const { inOrder: allOnClock, loading } = useClockedIn();
  // The owner clocks in like everyone else but does not take walk-ins, so
  // he is filtered out of the picker AND out of the count the "next
  // available" tile quotes — otherwise the tile promises a chair that is
  // not actually in the rotation.
  const inOrder = allOnClock.filter(
    ([slug]) => barbers.find((b) => b.slug === slug)?.takesWalkIns !== false
  );
  const count = inOrder.length;
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('service');
  const [serviceSlug, setServiceSlug] = useState<string | null>(null);
  const [addOnSlugs, setAddOnSlugs] = useState<string[]>([]);
  const [preferredBarber, setPreferredBarber] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckInResult | null>(null);

  const service = services.find((s) => s.slug === serviceSlug);

  // ── Reset ────────────────────────────────────────────────────────────
  // navigate('/kiosk') used to be the reset. It never was: the kiosk is
  // already AT /kiosk, so the router had nothing to change, the component
  // never remounted, and every field — name, phone, service, barber, the
  // result and its QR — stayed in memory under the next customer. Clearing
  // state explicitly is the only thing that actually empties the screen.
  const resetAll = useCallback(() => {
    setStep('service');
    setServiceSlug(null);
    setAddOnSlugs([]);
    setPreferredBarber(null);
    setName('');
    setPhone('');
    setBusy(false);
    setError(null);
    setResult(null);
  }, []);

  const [idlePrompting, setIdlePrompting] = useState(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Armed on every screen except the confirmation, which runs its own
  // shorter countdown, and except while nothing has been entered yet — an
  // empty service picker holds no one's data and can sit as long as it likes.
  const idleArmed = step !== 'result' && (step !== 'service' || Boolean(serviceSlug));

  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (!idleArmed || idlePrompting) return;
    idleTimer.current = setTimeout(() => setIdlePrompting(true), kiosk.idle_seconds * 1000);
  }, [idleArmed, idlePrompting, kiosk.idle_seconds]);

  useEffect(() => {
    resetIdleTimer();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [resetIdleTimer]);

  // Confirmation auto-return. Counts in view so the screen never just
  // vanishes mid-read.
  const [resetIn, setResetIn] = useState(kiosk.confirmation_reset_seconds);
  useEffect(() => {
    if (step !== 'result') {
      setResetIn(kiosk.confirmation_reset_seconds);
      return;
    }
    if (resetIn <= 0) {
      resetAll();
      return;
    }
    const id = window.setTimeout(() => setResetIn((n: number) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [step, resetIn, kiosk.confirmation_reset_seconds, resetAll]);

  const chosenAddOns = addOns.filter((a) => addOnSlugs.includes(a.slug));

  const totals = useMemo(() => {
    const minutes = (service?.duration_min ?? 0) + chosenAddOns.reduce((n, a) => n + a.duration_min, 0);
    const price = (service?.price ?? 0) + chosenAddOns.reduce((n, a) => n + a.price, 0);
    const custom = chosenAddOns.some((a) => 'custom_price' in a && a.custom_price);
    return { minutes, priceLabel: `$${price}${custom ? '+' : ''}` };
  }, [service, chosenAddOns]);

  const barberName = (slug: string | null) =>
    barbers.find((b) => b.slug === slug)?.display_name ?? '';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const fn = httpsCallable<Record<string, unknown>, CheckInResult>(getFns(), 'checkInWalkIn');
      const res = await fn({
        name: name.trim(),
        phone: toE164(phone),
        serviceSlug,
        addOnSlugs,
        preferredBarber,
      });
      setResult(res.data);
      setStep('result');
      // Deliberately no auto-hop to the waiting view: it fired in 2.5s,
      // long before anyone could scan the QR. The idle return below clears
      // the screen instead, and the customer keeps the link on their phone.
    } catch (err) {
      const e = err as { code?: string; message?: string };
      const msg = e.message ?? '';
      if (msg.includes('shop_empty')) {
        setError(t(c.errors.shopEmpty));
      } else if (e.code?.includes('already-exists')) {
        // Server sends the existing record's shortId in error details so the
        // customer can show it to their barber instead of checking in twice.
        const details = (e as { details?: { shortId?: string; status?: string } }).details;
        setError(t(c.errors.duplicate, { shortId: details?.shortId ?? '' }));
      } else {
        setError(t(c.errors.generic));
        console.error('checkInWalkIn failed:', e);
      }
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (!result) return;
    try {
      const fn = httpsCallable(getFns(), 'releaseBarber');
      await fn({ barberSlug: '', walkInId: result.walkInId, outcome: 'left' });
    } catch (err) {
      console.error('release on leave failed:', err);
    }
    resetAll();
  };

  // Confirmation is the one navy screen — it mirrors the phone status page
  // the customer is about to scan, so the two read as the same product. The
  // rest of the flow is the site's paper.
  const onNavy = step === 'result';
  useKioskGround(onNavy ? KIOSK_NAVY : KIOSK_PAPER);

  const shell = (children: React.ReactNode) => (
    <div
      onClick={resetIdleTimer}
      onTouchStart={resetIdleTimer}
      className={`kd-kiosk-root flex flex-col px-10 py-8 md:px-14 md:py-10 ${
        onNavy ? 'bg-navy text-paper' : 'bg-paper text-navy'
      }`}
    >
      <header className="flex shrink-0 items-center gap-4">
        <img
          src="/assets/logo/kd-crest.png"
          alt=""
          role="presentation"
          className="h-14 w-14 object-contain"
        />
        <span
          className={`font-oswald text-[18px] font-medium uppercase tracking-[0.22em] ${
            onNavy ? 'text-gold' : 'text-gold-text'
          }`}
        >
          {shop.name}
        </span>
      </header>

      <div className="flex-1 pt-8">{children}</div>

      {idlePrompting && (
        <IdlePrompt
          seconds={kiosk.idle_prompt_seconds}
          onStay={() => setIdlePrompting(false)}
          onExpire={() => {
            setIdlePrompting(false);
            resetAll();
          }}
        />
      )}
      {step !== 'service' && step !== 'result' && (
        <button
          type="button"
          onClick={() =>
            setStep(step === 'addOns' ? 'service' : step === 'barber' ? 'addOns' : 'barber')
          }
          className="mt-8 min-h-[56px] self-start font-newsreader text-[20px] text-slate-body underline underline-offset-4"
        >
          {t(c.back)}
        </button>
      )}
    </div>
  );

  // ── Step 1: service ──────────────────────────────────────────────────
  if (step === 'service') {
    return shell(
      <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
        {/* The site's crest-on-marble panel, same asset and treatment as the
            walk-ins section — the logo is the welcome, so there is no tag. */}
        <div
          className="relative hidden min-h-[320px] items-center justify-center overflow-hidden rounded-card bg-edge-1 bg-cover bg-center shadow-[0_24px_60px_rgba(11,27,58,0.14)] lg:flex"
          style={{ backgroundImage: "url('/assets/textures/marble.jpg')" }}
        >
          <span className="pointer-events-none absolute inset-[22px] rounded-2xl border-2 border-gold" />
          <img
            src="/assets/logo/kd-crest.png"
            alt=""
            role="presentation"
            className="relative w-[62%] max-w-[260px] object-contain drop-shadow-[0_20px_40px_rgba(11,27,58,0.3)]"
          />
        </div>

        <div>
          <h1 className="font-newsreader text-[44px] font-medium leading-[1.05] text-navy md:text-[56px]">
            {t(c.service.headline)}
          </h1>
          <p className="mt-4 font-newsreader text-[22px] text-slate-body">{t(c.service.subline)}</p>

          {/* Menu rows, like the site's price list: name, dotted leader,
              price. A grid of tiles made seven services look like a form. */}
          <ul className="mt-8 flex flex-col">
            {services.map((s) => (
              <li key={s.slug}>
                <button
                  type="button"
                  onClick={() => {
                    setServiceSlug(s.slug);
                    setStep('addOns');
                  }}
                  className="flex min-h-[72px] w-full items-baseline gap-4 border-b border-navy/15 py-4 text-left active:bg-navy/5"
                >
                  <span className="font-newsreader text-[24px] font-medium text-navy">
                    {serviceName(s)}
                  </span>
                  <span className="flex-1 border-b border-dotted border-navy/25" />
                  <span className="whitespace-nowrap font-newsreader text-[22px] italic text-navy/80">
                    {s.price_display}
                  </span>
                  <span className="w-[104px] whitespace-nowrap text-right font-oswald text-[18px] uppercase tracking-[0.16em] text-warm-muted">
                    {formatDuration(s.duration_min)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // ── Step 2  // ── Step 2: add-ons ──────────────────────────────────────────────────
  if (step === 'addOns') {
    return shell(
      <>
        <h1 className="font-newsreader text-[44px] font-medium leading-[1.05] text-navy md:text-[52px]">
          {t(c.addOns.headline)}
        </h1>
        <p className="mt-4 font-newsreader text-[22px] text-slate-body">{t(c.addOns.subline)}</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {addOns.map((a) => {
            const on = addOnSlugs.includes(a.slug);
            return (
              <button
                key={a.slug}
                type="button"
                onClick={() =>
                  setAddOnSlugs(
                    on ? addOnSlugs.filter((x) => x !== a.slug) : [...addOnSlugs, a.slug]
                  )
                }
                aria-pressed={on}
                // Selection is a solid navy fill, not a border tint: a
                // hairline is not a state you can read standing up.
                className={`min-h-[96px] rounded-card border-2 p-5 text-left transition ${
                  on ? 'border-gold bg-navy text-paper' : 'border-edge-2 bg-white text-navy'
                }`}
              >
                <p className="font-newsreader text-[22px] font-medium">{serviceName(a)}</p>
                <p className={`mt-1 font-oswald text-[18px] uppercase tracking-[0.14em] ${on ? 'text-gold' : 'text-warm-muted'}`}>
                  {a.price_display} · +{a.duration_min} min
                </p>
              </button>
            );
          })}
        </div>

        <p className="mt-8 font-newsreader text-[20px] text-slate-body">
          {t(c.addOns.runningTotal, {
            service: service?.name ?? '',
            addOns: chosenAddOns.length ? chosenAddOns.map((a) => a.name).join(', ') : '—',
            totalTime: formatDuration(totals.minutes),
            totalPrice: totals.priceLabel,
          })}
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-6">
          <button
            type="button"
            onClick={() => setStep('barber')}
            className="min-h-[64px] rounded-pill bg-gold px-12 py-4 font-newsreader text-[22px] text-navy"
          >
            {t(c.addOns.ctaContinue)}
          </button>
          <button
            type="button"
            onClick={() => {
              setAddOnSlugs([]);
              setStep('barber');
            }}
            className="min-h-[56px] font-newsreader text-[20px] text-slate-body underline underline-offset-4"
          >
            {t(c.addOns.ctaSkip)}
          </button>
        </div>
      </>
    );
  }

  // ── Step 3  // ── Step 3: barber (gates on live clock-in state) ────────────────────
  if (step === 'barber') {
    if (!loading && count === 0) {
      const nextOpen = hours.find((h) => h.open)?.open ?? '';
      return shell(
        <div role="status" className="grid h-full place-items-center text-center">
          <div>
            <p className="font-newsreader text-[44px] font-medium text-navy">
              {t(c.barber.emptyHeadline)}
            </p>
            <p className="mt-4 font-newsreader text-[22px] text-slate-body">
              {t(c.barber.emptySubline, { nextPostedOpen: nextOpen })}
            </p>
          </div>
        </div>
      );
    }

    const tile =
      'flex min-h-[260px] flex-col items-center justify-center rounded-card border-2 border-edge-2 bg-white p-6 text-center transition active:border-gold active:bg-navy/5';

    return shell(
      <>
        <h1 className="font-newsreader text-[44px] font-medium leading-[1.05] text-navy md:text-[52px]">
          {t(c.barber.headline)}
        </h1>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <button
            type="button"
            onClick={() => {
              setPreferredBarber(null);
              setStep('contact');
            }}
            className={tile}
          >
            {/* Same gold ring as a barber, so "no preference" reads as a
                chair you can pick rather than an opt-out. */}
            <span className="grid h-28 w-28 place-items-center rounded-full border-2 border-gold bg-paper font-newsreader text-[40px] text-navy">
              ✂
            </span>
            <p className="mt-4 font-newsreader text-[26px] font-medium text-navy">
              {t(c.barber.nextAvailable)}
            </p>
            <p className="mt-1 font-oswald text-[18px] uppercase tracking-[0.16em] text-warm-muted">
              {t(c.barber.onTheClock, { count })}
            </p>
          </button>

          {inOrder.map(([slug, state]) => {
            const b = barbers.find((x) => x.slug === slug);
            if (!b) return null;
            const free = state.currentStatus !== 'cutting';
            return (
              <button
                key={slug}
                type="button"
                onClick={() => {
                  setPreferredBarber(slug);
                  setStep('contact');
                }}
                className={tile}
              >
                <img
                  src={b.portrait.kiosk}
                  alt=""
                  className="h-28 w-28 rounded-full border-2 border-gold object-cover"
                />
                <p className="mt-4 font-newsreader text-[26px] font-medium text-navy">
                  {b.display_name}
                </p>
                <p
                  className={`mt-1 font-oswald text-[18px] uppercase tracking-[0.16em] ${
                    free ? 'text-gold-text' : 'text-warm-muted'
                  }`}
                >
                  {free ? t(c.barber.freeNow) : t(c.barber.withClient)}
                </p>
              </button>
            );
          })}
        </div>
      </>
    );
  }

  // ── Step 4  // ── Step 4: contact ──────────────────────────────────────────────────
  if (step === 'contact') {
    const canSubmit = !busy && name.trim().length > 0 && phone.replace(/\D/g, '').length === 10;
    const field =
      'mt-3 w-full min-h-[72px] rounded-card border border-edge-2 bg-white px-6 py-4 font-newsreader text-[26px] text-navy focus:border-navy focus:outline-none';
    const label = 'font-oswald text-[18px] font-medium uppercase tracking-[0.2em] text-gold-text';

    return shell(
      <div className="max-w-2xl">
        <h1 className="font-newsreader text-[44px] font-medium leading-[1.05] text-navy md:text-[52px]">
          {t(c.contact.headline)}
        </h1>
        <p className="mt-4 font-newsreader text-[22px] text-slate-body">{t(c.contact.subline)}</p>

        <label className="mt-10 block">
          <span className={label}>{t(c.contact.nameLabel)}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>

        <label className="mt-6 block">
          <span className={label}>{t(c.contact.phoneLabel)}</span>
          <input
            inputMode="numeric"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            className={field}
          />
        </label>

        {error && (
          <p role="alert" className="mt-6 font-newsreader text-[20px] text-red-accent">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="mt-10 min-h-[72px] rounded-pill bg-gold px-14 py-5 font-newsreader text-[26px] text-navy disabled:opacity-50"
        >
          {busy ? '…' : t(c.contact.ctaSubmit)}
        </button>
      </div>
    );
  }

  // ── Result  // ── Result ───────────────────────────────────────────────────────────
  if (!result) return shell(<p className="text-oncrest-soft">…</p>);

  // Both confirmation states resolve through the same function the phone
  // status page uses, so the screen in front of the customer and the screen
  // in their hand cannot describe one ticket differently.
  const status = walkInStatus(
    {
      assignedBarber: result.initialAssignment,
      preferredBarber,
      position: result.queuePosition,
      minutes: result.initialAssignment ? totals.minutes : null,
    },
    copy.walkInStatus,
    t,
    barberName
  );

  const faceSlug = result.initialAssignment ?? preferredBarber;
  const face = faceSlug ? barbers.find((b) => b.slug === faceSlug) : undefined;

  return shell(
    <div className="grid h-full place-items-center text-center">
      <div className="flex flex-col items-center">
        {/* Same gold-ringed portrait the phone status page leads with, so
            the kiosk and the screen they are about to scan match. */}
        {face && (
          <img
            src={face.portrait.kiosk}
            alt=""
            className="h-32 w-32 rounded-full border-2 border-gold object-cover"
          />
        )}

        <p className="mt-6 font-newsreader text-[52px] font-medium leading-[1.05] text-paper md:text-[64px]">
          {status.headline}
        </p>
        <p className="mt-4 font-newsreader text-[24px] text-oncrest-soft">{status.subline}</p>
        {status.position && (
          <p className="mt-4 font-newsreader text-[30px] text-paper">{status.position}</p>
        )}

        <QrHandoff shortId={result.shortId} />

        {!status.isUp && (
          <div className="mt-10 flex flex-wrap justify-center gap-5">
            <button
              type="button"
              onClick={() => navigate(`/kiosk/waiting/${result.walkInId}`)}
              className="min-h-[64px] rounded-pill bg-gold px-12 py-4 font-newsreader text-[22px] text-navy"
            >
              {t(c.result.ctaWait)}
            </button>
            <button
              type="button"
              onClick={leave}
              className="min-h-[64px] rounded-pill border-2 border-paper/40 px-12 py-4 font-newsreader text-[22px] text-paper"
            >
              {t(c.result.ctaLeave)}
            </button>
          </div>
        )}

        {/* Always reachable: the countdown says what is about to happen and
            Done lets someone leave immediately instead of standing over a
            screen holding their phone number. */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={resetAll}
            className="min-h-[64px] rounded-pill bg-gold px-14 py-5 font-newsreader text-[24px] text-navy"
          >
            {t(c.reset.done)}
          </button>
          <p aria-live="polite" className="font-oswald text-[18px] uppercase tracking-[0.18em] text-oncrest-muted">
            {t(c.reset.countdown, { seconds: resetIn })}
          </p>
        </div>
      </div>
    </div>
  );
}
